import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "./keys.js";
import { controlFetch } from "@/lib/api/client";
import type {
  GetTenantPluginCredentialHttpRequest,
  GetTenantPluginCredentialHttpResponse,
  GetTenantPluginConfigHttpRequest,
  GetTenantPluginConfigHttpResponse,
  ListPluginManifestsHttpRequest,
  ListPluginManifestsHttpResponse,
  SaveTenantPluginCredentialHttpRequest,
  SaveTenantPluginCredentialHttpResponse,
  SaveTenantPluginConfigHttpRequest,
  SaveTenantPluginConfigHttpResponse,
  ValidateTenantPluginHttpRequest,
  ValidateTenantPluginHttpResponse,
} from "../../../../../packages/shared-types/src/plugin-control-plane.js";

export function usePluginManifestsQuery(pluginId?: string) {
  return useQuery({
    queryKey: queryKeys.pluginManifests(pluginId),
    queryFn: async () =>
      await controlFetch<ListPluginManifestsHttpResponse>("/api/plugins/manifests", pluginId ? { pluginId } : {}),
  });
}

export function useTenantPluginConfigQuery(tenantId: string, pluginId: string) {
  return useQuery({
    queryKey: queryKeys.tenantPluginConfig(tenantId, pluginId),
    queryFn: async () =>
      await controlFetch<GetTenantPluginConfigHttpResponse>(
        "/api/plugins/config/get",
        { tenantId, pluginId },
      ),
    enabled: Boolean(tenantId && pluginId),
  });
}

export function useTenantPluginCredentialQuery(
  tenantId: string,
  pluginId: string,
  credentialKey: string,
) {
  return useQuery({
    queryKey: queryKeys.tenantPluginCredential(tenantId, pluginId, credentialKey),
    queryFn: async () =>
      await controlFetch<GetTenantPluginCredentialHttpResponse>(
        "/api/plugins/credential/get",
        { tenantId, pluginId, credentialKey },
      ),
    enabled: Boolean(tenantId && pluginId && credentialKey),
  });
}

export function useSaveTenantPluginConfigMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveTenantPluginConfigHttpRequest) =>
      await controlFetch<SaveTenantPluginConfigHttpResponse>(
        "/api/plugins/config/save",
        input,
      ),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.tenantPluginConfig(saved.tenantId, saved.pluginId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.tenantRuntimeTarget(saved.tenantId),
      });
    },
  });
}

export function useSaveTenantPluginCredentialMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveTenantPluginCredentialHttpRequest) =>
      await controlFetch<SaveTenantPluginCredentialHttpResponse>(
        "/api/plugins/credential/save",
        input,
      ),
    onSuccess: (saved, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.tenantPluginCredential(
          variables.tenantId,
          saved.pluginId,
          saved.credentialKey,
        ),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.tenantRuntimeTarget(variables.tenantId),
      });
    },
  });
}

export function useValidateTenantPluginMutation() {
  return useMutation({
    mutationFn: async (input: ValidateTenantPluginHttpRequest) =>
      await controlFetch<ValidateTenantPluginHttpResponse>(
        "/api/plugins/validate",
        input,
      ),
  });
}
